import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Dialog } from '@mui/material'
import { toast } from 'react-toastify'
import SearchableSelect from 'components/SearchableSelect'
import * as estateDocumentsService from 'services/estateDocumentsService'
import { loadEstateOwnedFilesRoutine, loadEstateFoldersRoutine } from 'ducks/estateDocuments/actions'
import { font, ink, line, radius, surface } from 'features/dashboard/tokens'

const CYAN = '#33becc'
const UNASSIGNED_VALUE = '__unassigned__'

const flattenFolders = (nodes, ancestors = []) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({ id: node.id, name: node.name, path: ancestors })
    return acc.concat(flattenFolders(node.children, [...ancestors, node.name]))
  }, [])

const EstateBulkMoveFilesModal = ({ estateId, fileIds, folders, sourceFolderId, onClose, onMoved }) => {
  const dispatch = useDispatch()
  const [target, setTarget] = useState(null)
  const [moving, setMoving] = useState(false)
  const count = fileIds.length
  const canUnassign = Boolean(sourceFolderId)

  const options = useMemo(() => {
    const flat = flattenFolders(folders).filter(folder => folder.id !== sourceFolderId)
    const folderOptions = flat.map(folder => ({
      value: folder.id,
      label: folder.name,
      sublabel: folder.path.length ? folder.path.join(' / ') : undefined
    }))
    return canUnassign
      ? [{ value: UNASSIGNED_VALUE, label: 'All documents (unassigned)' }, ...folderOptions]
      : folderOptions
  }, [folders, sourceFolderId, canUnassign])

  const handleMove = async () => {
    if (!target || moving) return
    setMoving(true)

    const results = await Promise.allSettled(fileIds.map(fileId => (
      target === UNASSIGNED_VALUE
        ? estateDocumentsService.unassignEstateFileFromFolder({ estateId, folderId: sourceFolderId, fileId })
        : estateDocumentsService.assignEstateFileToFolder({ estateId, folderId: target, fileId })
    )))

    const succeeded = results.filter(result => result.status === 'fulfilled').length
    const failed = results.length - succeeded

    if (failed === 0) {
      toast.success(`Moved ${succeeded} document${succeeded === 1 ? '' : 's'}.`)
    } else if (succeeded === 0) {
      toast.error(`Could not move ${failed} document${failed === 1 ? '' : 's'}.`)
    } else {
      toast.warning(`${succeeded} of ${results.length} documents moved, ${failed} failed.`)
    }

    dispatch(loadEstateOwnedFilesRoutine({ estateId, folderId: sourceFolderId, page: 1, reloadLoaded: true }))
    dispatch(loadEstateFoldersRoutine({ estateId }))
    setMoving(false)
    onMoved()
    onClose()
  }

  return (
    <Dialog open onClose={moving ? undefined : onClose} maxWidth='xs' fullWidth>
      <Container>
        <Title>Move {count} document{count === 1 ? '' : 's'}</Title>
        <Body>Choose a folder to move the selected documents to.</Body>

        {options.length === 0 ? (
          <Empty>There are no other folders to move them to yet.</Empty>
        ) : (
          <Field>
            <FieldLabel htmlFor='estate-bulk-move-folder'>Folder</FieldLabel>
            <SearchableSelect
              id='estate-bulk-move-folder'
              value={target}
              onChange={setTarget}
              options={options}
              placeholder='Choose a folder…'
              searchPlaceholder='Search folders…'
              ariaLabel='Folder to move the selected documents to'
            />
          </Field>
        )}

        <Footer>
          <CancelButton type='button' onClick={onClose} disabled={moving}>
            Cancel
          </CancelButton>
          <SaveButton type='button' onClick={handleMove} disabled={moving || !target}>
            {moving ? 'Moving…' : `Move ${count} document${count === 1 ? '' : 's'}`}
          </SaveButton>
        </Footer>
      </Container>
    </Dialog>
  )
}

export default EstateBulkMoveFilesModal

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: ${surface.card};
`

const Title = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Body = styled.p`
  margin: 0;
  font-size: ${font.body};
  color: ${ink.mute};
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const Empty = styled.p`
  margin: 0;
  padding: 10px 0;
  font-size: ${font.body};
  color: ${ink.mute};
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: color 160ms ease;

  &:hover:not(:disabled) {
    color: ${ink.base};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const SaveButton = styled.button`
  min-height: 40px;
  padding: 0 22px;
  border: none;
  border-radius: ${radius.sm};
  background: ${CYAN};
  color: #ffffff;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: background 160ms ease, opacity 160ms ease;

  &:hover:not(:disabled) {
    background: #2aa6b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`
